import type { AttributeHistoryResponse, ConfidenceLevel } from '../../api/types';
import { describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../StateMessage';
import { CONFIDENCE_COPY, TRAJECTORY_COPY, bandFor, formatTimestamp, sourceLabel } from './attributeDisplay';
import { AttributeHistoryPlot } from './AttributeHistoryPlot';
import styles from './AttributeHistory.module.css';

/**
 * One attribute's recorded history, for one person
 * (docs/UX_ARCHITECTURE.md §3.9, the MVP part of that section).
 *
 * This closes the gap the profile had until now: the row could say
 * "Improving" and could say "4 assessment points", and a reader had no way
 * to see the four points. A stated direction that cannot be traced back to
 * the records behind it is exactly the kind of unexplained conclusion
 * docs/PRODUCT_CONSTITUTION.md rules out.
 *
 * ---------------------------------------------------------------------
 * WHAT A ROW IN THIS TABLE IS
 *
 * Not one assessment. Each row is the **composite as it stood on that
 * date**, recomputed from every assessment recorded up to and including it
 * by the same rules that produce the current state. The table says so in
 * its caption, because a reader who assumes each row is a single person's
 * rating would misread every one of them.
 *
 * The per-source values that made each composite travel with it, so the
 * perspectives behind a date are visible rather than folded away. Where a
 * source contributed several records, that count is shown too.
 *
 * WHAT THIS COMPONENT DOES NOT COMPUTE
 *
 * Everything derived here arrives derived. The direction and the delta are
 * the domain's (`domain/src/rules/trajectory.ts`), taken from the response
 * verbatim. This component does not classify improving/declining/stable,
 * does not compute a change between any pair of points, does not total,
 * average or rank anything, and invents no "growth score", "improvement
 * percentage", "peak" or "best ever" — none of which exist in the domain.
 * The one thing it derives is the band label for a value, which
 * docs/ASSESSMENT_FRAMEWORK.md §2.2 defines as a display aid derived from
 * the rating (the same exception AttributeRow already takes).
 *
 * ON `spread` AND `highVariance` AT A HISTORY POINT
 *
 * Both are computed over every usable record up to that date — including a
 * single source's own earlier ratings. So at Tobias's 2026 point the spread
 * of 5 is the range across four years of his manager's ratings plus one
 * peer's, not a disagreement of five points between two people. The flag is
 * therefore worded as a spread in the recorded ratings, and never as
 * "sources disagree" — which is what it would mean only when every record
 * shares a date. The per-source values in the same row are the honest
 * answer to "who said what".
 * ---------------------------------------------------------------------
 */
export function AttributeHistory({
  employeeId,
  attributeDefinitionId,
  attributeName,
  regionId,
}: {
  employeeId: string;
  attributeDefinitionId: string;
  attributeName: string;
  regionId: string;
}) {
  // Fetched only once this panel is open. The profile lists the whole
  // attribute library, and eagerly loading a history for every row would be
  // 23 requests nobody asked for.
  const state = useApiResource<AttributeHistoryResponse>(
    `/employees/${employeeId}/attributes/${attributeDefinitionId}/history`
  );

  if (state.status === 'loading') {
    return (
      <div className={styles.panel} id={regionId}>
        <LoadingState label="Loading recorded history…" />
      </div>
    );
  }

  if (state.status === 'failed') {
    return (
      <div className={styles.panel} id={regionId}>
        <ErrorState title="This history could not be loaded" detail={describeError(state.error)} />
      </div>
    );
  }

  const { points, trajectory } = state.data;
  const trajectoryCopy = TRAJECTORY_COPY[trajectory.direction];

  if (points.length === 0) {
    return (
      <div className={styles.panel} id={regionId}>
        <EmptyState
          title="No assessments recorded"
          detail={`Nothing has been recorded for ${attributeName}, so there is no history to show. This is an absence of records, not a history of low ratings.`}
        />
      </div>
    );
  }

  const latestIndex = points.length - 1;

  return (
    <div className={styles.panel} id={regionId}>
      <AttributeHistoryPlot points={points} attributeName={attributeName} />

      {/* The domain's conclusion, restated where the points are visible —
          not recalculated from them. */}
      <p className={styles.trajectory}>
        <span className={styles.trajectoryLabel}>Trajectory</span>{' '}
        {points.length < 2 ? (
          <>
            {trajectoryCopy.label} — one recorded point is not enough to establish a direction, and this is not the
            same as a direction of "no change".
          </>
        ) : (
          <>
            {trajectoryCopy.label}
            {trajectory.delta !== null
              ? ` — ${trajectory.delta > 0 ? '+' : ''}${trajectory.delta} between the two most recent points`
              : ''}
            . This is the system's own classification of the points below; it is not recalculated here.
          </>
        )}
      </p>

      <table className={styles.table}>
        <caption className={styles.caption}>
          Every recorded point, oldest first. Each row is the composite as it stood on that date — recomputed from
          all assessments recorded up to then, not a single person's rating — with the sources that made it.
        </caption>
        <thead>
          <tr>
            <th scope="col">Recorded on</th>
            <th scope="col">Composite</th>
            <th scope="col">Band</th>
            <th scope="col">Confidence</th>
            <th scope="col">Sources at this point</th>
            <th scope="col">Notes</th>
          </tr>
        </thead>
        <tbody>
          {points.map((point, index) => {
            const band = bandFor(point.composite.composite);
            const isLatest = index === latestIndex;
            return (
              <tr key={point.asOf} className={isLatest ? styles.latest : undefined}>
                <th scope="row">
                  {formatTimestamp(point.asOf)}
                  {isLatest ? (
                    <span
                      className={styles.currentTag}
                      title="The most recent recorded point — this is the value shown on the row above."
                    >
                      Current
                    </span>
                  ) : (
                    <span className={styles.earlierTag}>Earlier</span>
                  )}
                </th>
                <td className={styles.numeric}>{point.composite.composite.toFixed(1)}</td>
                <td>{band.label}</td>
                <td title={CONFIDENCE_COPY[point.confidence.level].meaning}>
                  {CONFIDENCE_WORD[point.confidence.level]}
                </td>
                <td>
                  <ul className={styles.sources}>
                    {point.composite.contributors.map((contributor) => (
                      <li key={contributor.sourceType}>
                        {sourceLabel(contributor.sourceType)} {contributor.value}
                        {contributor.recordCount > 1 ? (
                          <span className={styles.recordCount}> ({contributor.recordCount} records)</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </td>
                <td>
                  {point.confidence.highVariance ? (
                    <span
                      className={styles.flag}
                      title="The ratings recorded up to this date span more than the policy threshold. That range can come from different sources disagreeing, or from one source's own rating moving over time — the per-source values in this row are what distinguishes the two."
                    >
                      Wide spread in recorded ratings
                    </span>
                  ) : (
                    <span className={styles.none}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The confidence level as a single word, for a column already headed
 * "Confidence" — where attributeDisplay's "Low confidence" would repeat the
 * header in every cell. The authoritative wording is still what the reader
 * gets on demand: each cell carries CONFIDENCE_COPY's own definition as its
 * title, so nothing here becomes a second, drifting vocabulary.
 */
const CONFIDENCE_WORD: Record<ConfidenceLevel, string> = {
  unconfirmed: 'Unconfirmed',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};
